import { useState, useEffect } from "react";
import { DateRangePicker } from "../../shared/dateRangePicker";
import { SearchSelect } from "../../shared/searchSelect";
import { SearchFiltersModal } from "../../shared/searchFiltersModal";
import stringHelpers from "../../../utilities/stringHelpers";
import { toDateString } from "../../../utilities/dateUtilities";
import Api from "../../api/api";
import { TagSelector } from "../../shared/tagSelector";
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities";
import arrayUtilities from "../../../utilities/arrayUtilities";

export const CameraDataFiltersModal = ({
	showModal,
	handleClose,
	handleConfirm,
	initialCameraFromDate,
	initialCameraToDate,
	initialSiteId,
	initialSelectedCamera,
	initialCameras,
	initialSelectedTags = [],
	initialDirection,
	initialAppliedFilters,
	sites = [],
	tags = [],
	organisationId,
	token
}) => {
	const [cameraFromDate, setCameraFromDate] = useState(initialCameraFromDate)
	const [cameraToDate, setCameraToDate] = useState(initialCameraToDate)
	const [siteId, setSiteId] = useState(initialSiteId)
	const [selectedCamera, setSelectedCamera] = useState(initialSelectedCamera)
	const [cameras, setCameras] = useState(initialCameras)
	const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
	const [direction, setDirection] = useState(initialDirection)
	const [directionOptions, setDirectionOptions] = useState([])
	const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

	useEffect(() => {
		Api.getCameraTags(
			data => setDirectionOptions((data || []).map(item => ({ value: item, label: item[0].toUpperCase() + item.substring(1) }))),
			error => console.log(error),
			organisationId,
			token
		)
	}, [])

	const handleTagsChange = tagSelections => {
		setSelectedTags(tagSelections)
		setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
	}

	const getCameras = (newSiteId) => {
		if (!stringHelpers.isNullOrEmpty(newSiteId)) {
			Api.getSiteCameras(
				data => {
					setCameras(data)
				},
				error => {
					console.log(error)
				},
				organisationId,
				newSiteId,
				token
			)
		} else {
			Api.getCameras(
				0,
				data => {
					setCameras(data)
				},
				error => {
					console.log(error)
				},
				organisationId,
				token
			)
		}
		setSelectedCamera("")
	}

	const clearLocalFilters = () => {
		setCameraFromDate(null)
		setCameraToDate(null)
		setSiteId("")
		setSelectedCamera("")
		setSelectedTags([])
		setDirection("")
		setAppliedFilters(null)
		getCameras("")
	}

	return (
		<SearchFiltersModal
			title="Filter Results"
			handleClose={ handleClose }
			handleConfirm={ () => handleConfirm({ cameraFromDate, cameraToDate, siteId, selectedCamera, cameras, selectedTags, direction, appliedFilters }) }
			showModal={ showModal }
			key="camera-data-filters-modal"
			handleReset={ clearLocalFilters }
		>
			<div className="w-100">
				<div className="mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
					<DateRangePicker
						fromDate={ cameraFromDate }
						toDate={ cameraToDate }
						setDateRange={ (firstDate, lastDate, presetLabel) => {
							const isFirstDateEmpty = firstDate === null || firstDate === ""
							const isLastDateEmpty = lastDate === null || lastDate === ""
							setCameraFromDate(isFirstDateEmpty ? null : firstDate);
							setCameraToDate(isLastDateEmpty ? null : lastDate);
							if (!stringHelpers.isNullOrEmpty(presetLabel)) {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters.From
									delete updatedFilters.To
									updatedFilters["Date Range"] = presetLabel
									return updatedFilters
								})
							}
							else {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									if (!isFirstDateEmpty && !isLastDateEmpty) {
										delete updatedFilters.From
										delete updatedFilters.To
										updatedFilters["Date Range"] = `${toDateString(new Date(firstDate))} - ${toDateString(new Date(lastDate))}`
									} else {
										if (!isFirstDateEmpty) {
											updatedFilters.From = toDateString(new Date(firstDate))
										} else {
											delete updatedFilters.From
										}
										if (!isLastDateEmpty) {
											updatedFilters.To = toDateString(new Date(lastDate))
										} else {
											delete updatedFilters.To
										}
										delete updatedFilters["Date Range"]
									}
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						}}
						showClearOption
						hideLabel
						fullWidth
						dropdownPositionRight
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Site</label>
					<SearchSelect
						defaultLabel="All"
						width="100%"
						options={ [{ value: "", label: "All" }, ...sites.map(item => ({ value: item.id, label: item.name }))] }
						onSelect={ value => {
							const newSiteId = !stringHelpers.isNullOrEmpty(value) ? value : ""
							setSiteId(newSiteId)
							getCameras(newSiteId)
							if (!stringHelpers.isNullOrEmpty(value)) {
								const selectedSite = sites.find(item => item.id === value)
								setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite?.name }))
							} else {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters["Site"]
									delete updatedFilters["Camera"]
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						} }
						selected={ !stringHelpers.isNullOrEmpty(siteId) ? siteId : null }
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Camera</label>
					<SearchSelect
						defaultLabel={ stringHelpers.isNullOrEmpty(siteId) ? "Select a site first" : "All" }
						isDisabled={ stringHelpers.isNullOrEmpty(siteId) }
						width="100%"
						options={ [{ value: "", label: "All" }, ...cameras.map(item => ({ value: item.macAddress, label: item.name }))] }
						onSelect={ value => {
							const newSelectedCamera = !stringHelpers.isNullOrEmpty(value) ? value : ""
							setSelectedCamera(newSelectedCamera)
							if (!stringHelpers.isNullOrEmpty(value)) {
								const selectedCameraItem = cameras.find(item => item.macAddress === value)
								setAppliedFilters(prevState => ({ ...prevState, "Camera": selectedCameraItem?.name }))
							} else {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters["Camera"]
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						} }
						selected={ !stringHelpers.isNullOrEmpty(selectedCamera) ? selectedCamera : null }
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Direction</label>
					<SearchSelect
						defaultLabel="All"
						width="100%"
						options={ [{ value: "", label: "All" }, ...directionOptions] }
						onSelect={ value => {
							const newDirection = !stringHelpers.isNullOrEmpty(value) ? value : ""
							setDirection(newDirection)
							if (!stringHelpers.isNullOrEmpty(value)) {
								const selectedDirection = directionOptions.find(item => item.value === value)
								setAppliedFilters(prevState => ({ ...prevState, "Direction": selectedDirection?.label }))
							} else {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters["Direction"]
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						} }
						selected={ !stringHelpers.isNullOrEmpty(direction) ? direction : null }
					/>
				</div>

				{ !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
					<div className="d-flex align-items-center justify-content-between">
						<label className="m-0 mr-2 mb-1 nowrap">Tags</label>
						<button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
					</div>
					<TagSelector
						onChange={ handleTagsChange }
						selectedTags={ selectedTags }
						tags={ tags }
					/>
				</div> }
			</div>
		</SearchFiltersModal>
	)
}
